// @flow

import type { ComponentType } from 'react';
import type { ClientContext } from 'app/common/lib/context';
import type { Deeplinks } from 'app/common/lib/deeplinkRoutes';
import validateChildRouteOrder from 'app/common/lib/ReactRouter/validateChildRouteOrder';
import type {
  AsyncOnEnter,
  OnLeave,
  OnChange,
  OnEnter,
  Replace,
  GetComponent,
  PinterestPlainRoute,
  PromiseLoader,
  State,
  SyncOnEnter,
} from 'app/common/lib/ReactRouter/types';

export class Route {
  path: ?string; // IndexRoutes don't have a path
  enterHooks: Array<OnEnter>;
  props: Object;
  onChange: ?OnChange;
  leaveHooks: Array<OnLeave>;
  indexRoute: ?Route;
  childRoutes: ?Array<Route>;
  legacy_server_context: ClientContext;
  deeplinks: ?Deeplinks;
  pageType: ?string;
  getComponent: ?GetComponent;
  component: ?ComponentType<*>;
  authBundleLoader: ?PromiseLoader; // Only used for unauth routes
  authResources: ?Array<{
    name: string,
    options: (props: *) => *,
  }>;
  bundle: ?string;
  chunkName: ?string | ((State) => ?string);

  constructor({
    path,
    onEnter,
    props = {},
    onChange,
    onLeave,
    indexRoute,
    childRoutes,
    legacy_server_context,
    deeplinks,
    pageType,
    getComponent,
    component,
    authBundleLoader,
    authResources,
    bundle,
    chunkName,
  }: PinterestPlainRoute) {
    if (__SERVER__ && process.env.NODE_ENV !== 'production') {
      validateChildRouteOrder((childRoutes: any)).forEach((message) => {
        // eslint-disable-next-line no-console
        console.warn(`[ngjs Route Order] ${message}`);
      });
    }

    this.path = path;
    this.enterHooks = onEnter ? [onEnter] : [];
    this.onChange = onChange;
    this.leaveHooks = onLeave ? [onLeave] : [];
    this.props = props;
    this.indexRoute = indexRoute;
    this.childRoutes = childRoutes;
    this.legacy_server_context = legacy_server_context;
    this.deeplinks = deeplinks;
    this.pageType = pageType;
    this.getComponent = getComponent;
    this.component = component;
    this.authBundleLoader = authBundleLoader;
    this.authResources = authResources;
    this.bundle = bundle;
    this.chunkName = chunkName;
  }

  // Unnecessary in react-router v4
  wrapReplace(replace: Replace): Replace {
    return (location, ...args) => {
      if (typeof location === 'string') {
        location = encodeURI(location);
      } else {
        location.pathname = encodeURI(location.pathname);
      }

      return replace(location, ...args);
    };
  }

  onEnter: AsyncOnEnter = (nextState, replace, callback: () => void) => {
    let async = false;
    const replaceState = this.wrapReplace(replace);

    this.enterHooks.forEach((hook) => {
      if (hook.length === 3) {
        async = true;
        (hook: AsyncOnEnter).call(this, nextState, replaceState, callback);
      } else {
        // $FlowIssue type narrowing on hook.length doesn't work
        (hook: SyncOnEnter).call(this, nextState, replaceState);
      }
    });
    if (!async) {
      callback();
    }
  };

  addEnterHook(enterHook: OnEnter): void {
    this.enterHooks.push(enterHook);
  }
}

export class Redirect extends Route {
  constructor(path: string, onEnter: OnEnter, legacy_server_context: ClientContext) {
    super({ path, onEnter, legacy_server_context });
  }
}
